import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'grommet';
import ModalRenderer from './ModalRenderer';

// --- Standard onboarding helpers ---
function renderScope(offerings, platforms, channels, cidPlanDisplayLabel, uploaderDisplayLabel) {
  const items = [];
  if (offerings.useCid) {
    items.push(
      <li key="cid" style={{ paddingTop: '0.5rem' }}>
        YouTube Content ID monitoring and enforcement.
        <ul>
          <li>
            Pricing Plan:&nbsp;
            <strong>{cidPlanDisplayLabel}</strong>
          </li>
          <li>
            Upload Option:&nbsp;
            <strong>{uploaderDisplayLabel}</strong>
          </li>
        </ul>
      </li>,
    );

    if (channels && channels.length > 0) {
      items.push(
        <li key="cid-channels" style={{ paddingTop: '0.5rem' }}>
          Configured YouTube Channels:
          <ul>
            {channels.filter(Boolean).map((ch) => (
              <li key={ch}>
                <a href={ch} target="_blank" rel="noopener noreferrer">
                  {ch}
                </a>
              </li>
            ))}
          </ul>
        </li>,
      );
    }
  }

  if (offerings.useSocial) {
    items.push(
      <li key="social" style={{ paddingTop: '0.5rem' }}>
        Scanning and takedown enforcement on selected platforms:
        <ul>
          {platforms.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </li>,
    );
  }
  return items;
}

function renderRevenueShare(offerings, revenueSharePercent, hybridDetails, isHybrid) {
  if (!offerings.useCid) return null;
  if (isHybrid && hybridDetails) {
    return (
      <li>
        <strong>Revenue Share (YouTube Content ID)</strong>
        <ul style={{ paddingTop: '0.5rem' }}>
          <li>
            Revenue share plan:&nbsp;
            <strong>
              {hybridDetails.initialSharePercent}
              % for&nbsp;
              {hybridDetails.initialMonths}
              &nbsp;months, then&nbsp;
              {hybridDetails.afterSharePercent}
              %
            </strong>
          </li>
          <li>
            Revenue share calculations shall be subject to YouTube&apos;s reporting,
            payment schedules, deductions, and applicable platform fees.
          </li>
        </ul>
      </li>
    );
  }
  return (
    <li>
      <strong>Revenue Share (YouTube Content ID)</strong>
      <ul style={{ paddingTop: '0.5rem' }}>
        <li>
          Revenue share plan:&nbsp;
          <strong>
            {revenueSharePercent}
            %
          </strong>
          &nbsp;of recovered revenue.
        </li>
        <li>
          Revenue share calculations shall be subject to YouTube&apos;s reporting,
          payment schedules, deductions, and applicable platform fees.
        </li>
      </ul>
    </li>
  );
}

// --- Prefill onboarding helpers ---
function renderPrefillProductScope(products) {
  if (!products?.length) {
    return <li>Services as specified in the onboarding configuration.</li>;
  }
  return products.map((product) => {
    const subItems = [];

    if (product.plan?.name) {
      subItems.push(
        <li key="plan">
          Plan:&nbsp;
          <strong>{product.plan.name}</strong>
        </li>,
      );
    }

    if (product.productType === 'youtube_cid') {
      if (product.config?.uploader) {
        subItems.push(
          <li key="uploader">
            Upload managed by:&nbsp;
            <strong>{product.config.uploader}</strong>
          </li>,
        );
      }
      const cidChannels = product.config?.channels;
      if (cidChannels?.length) {
        subItems.push(
          <li key="channels">
            Configured YouTube Channels:
            <ul>
              {cidChannels.map((ch) => (
                <li key={ch}>
                  <a href={ch} target="_blank" rel="noopener noreferrer">{ch}</a>
                </li>
              ))}
            </ul>
          </li>,
        );
      }
      if (product.config?.revenueSharePercent != null) {
        subItems.push(
          <li key="rev-share">
            Revenue share:&nbsp;
            <strong>{`$${product.config.revenueSharePercent}%`}</strong>
            &nbsp;of recovered revenue. Revenue share calculations shall
            be subject to YouTube&apos;s reporting, payment schedules,
            deductions, and applicable platform fees.
          </li>,
        );
      }
    }

    if (product.productType === 'social') {
      const scanPlatforms = product.config?.platforms || product.plan?.data?.platforms;
      if (scanPlatforms?.length) {
        subItems.push(
          <li key="platforms">
            Monitored platforms:
            <ul>
              {scanPlatforms.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </li>,
        );
      }
    }

    return (
      <li key={product.productKey || product.productLabel} style={{ paddingTop: '0.5rem' }}>
        <strong>{product.productLabel}</strong>
        {subItems.length > 0 && <ul style={{ paddingTop: '0.5rem' }}>{subItems}</ul>}
      </li>
    );
  });
}

function renderPrefillFees(extraItems, currency) {
  if (!extraItems?.length) {
    return <li>As agreed upon execution of this Agreement.</li>;
  }

  return extraItems.map((item) => {
    const isDiscount = item.kind === 'discount';
    let amountLabel;
    if (item.valueType === 'percent') {
      amountLabel = `${isDiscount ? '-' : ''}${item.value}%`;
    } else {
      amountLabel = `${isDiscount ? '-' : ''}${currency} $${item.value}`;
    }
    const freqLabel = item.frequency === 'one_time' ? '(one-time)' : '(monthly)';

    return (
      <li key={`${item.description}-${item.frequency}-${item.value}`}>
        {item.description}
        :&nbsp;
        <strong>{amountLabel}</strong>
        &nbsp;
        {freqLabel}
      </li>
    );
  });
}

// --- Shared boilerplate sections ---
const serviceLimitationsContent = (
  <li>
    <strong>Service Limitations and Disclaimers</strong>
    <ul style={{ paddingTop: '0.5rem' }}>
      <li>
        Supplier does not guarantee the identification, detection, or removal
        of all instances of infringing content.
      </li>
      <li>
        All enforcement activities are performed on a&nbsp;
        <strong>commercially reasonable efforts basis</strong>
        .
      </li>
      <li>
        Services are subject to the policies, technical limitations,
        and response times of third-party platforms.
      </li>
      <li>
        Supplier does not provide legal advice, legal opinions, or legal representation.
      </li>
    </ul>
  </li>
);

const taxesContent = (
  <li style={{ marginBottom: '2.5rem' }}>
    <strong>Taxes</strong>
    <ul style={{ paddingTop: '0.5rem' }}>
      <li>
        All fees and revenue shares are exclusive of applicable taxes, duties,
        or governmental charges, which shall be the responsibility of the
        Customer unless otherwise required by law.
      </li>
    </ul>
  </li>
);

// --- Component ---
const ExhibitAModal = ({
  toggle,
  isSmall,
  // Standard-mode props
  offerings = null,
  platforms = null,
  channels = null,
  effectiveDate = '',
  setupFee = null,
  monthlyFee = null,
  currency = 'USD',
  revenueSharePercent = null,
  cidPlanDisplayLabel = null,
  uploaderDisplayLabel = null,
  hybridDetails = null,
  isHybrid = false,
  // Prefill-mode prop — when set, all standard props above are unused
  prefillExhibitA = null,
}) => {
  const isPrefill = !!prefillExhibitA;
  const resolvedEffectiveDate = isPrefill ? prefillExhibitA.effectiveDate : effectiveDate;
  const resolvedCurrency = isPrefill ? (prefillExhibitA.currency || 'USD') : currency;

  return (
    <ModalRenderer
      toggle={toggle}
      isSmall={isSmall}
      title="Exhibit A"
      background="var(--page-modal-bg)"
      textColor="var(--page-modal-text)"
      renderContent={() => (
        <Box pad="1rem" margin={{ bottom: '1rem' }}>
          <h2 style={{ marginBlockEnd: '0.5rem', marginBlockStart: '0.5rem' }}>
            <strong>Description of Services and Fees</strong>
          </h2>
          <ul>
            <li>
              <strong>Service Provider</strong>
              <ul style={{ paddingTop: '0.5rem' }}>
                <li>
                  Redflag (&quot;Supplier&quot;) shall provide the Customer with access to&nbsp;
                  <strong>Cyclops</strong>
                  , a software-as-a-service (SaaS) platform designed to support content
                  protection, anti-piracy enforcement, and YouTube Content ID monitoring.
                </li>
              </ul>
            </li>
            <br />
            <li>
              <strong>Scope of Services</strong>
              <ul style={{ paddingTop: '0.5rem' }}>
                {isPrefill
                  ? renderPrefillProductScope(prefillExhibitA.products)
                  : renderScope(
                    offerings, platforms, channels, cidPlanDisplayLabel, uploaderDisplayLabel,
                  )}
              </ul>
            </li>
            <br />
            {serviceLimitationsContent}
            <br />
            <li>
              <strong>Effective Date</strong>
              <ul style={{ paddingTop: '0.5rem' }}>
                <li>
                  The Services under this&nbsp;
                  <strong>Exhibit A</strong>
                  &nbsp;shall commence on&nbsp;
                  <strong>{resolvedEffectiveDate}</strong>
                  , unless otherwise agreed in writing by the parties.
                </li>
              </ul>
            </li>
            <br />
            <li>
              <strong>Fees</strong>
              <ul style={{ paddingTop: '0.5rem' }}>
                {isPrefill ? (
                  renderPrefillFees(prefillExhibitA.extraItems, resolvedCurrency)
                ) : (
                  <>
                    <li>
                      One-time setup fee:&nbsp;
                      <strong>
                        {resolvedCurrency}
                        &nbsp;$
                        {setupFee}
                      </strong>
                      , payable upon execution of the Agreement.
                    </li>
                    <li>
                      Monthly service fee:&nbsp;
                      <strong>
                        {resolvedCurrency}
                        &nbsp;$
                        {monthlyFee}
                      </strong>
                      , covering the selected Services in this&nbsp;
                      <strong>Exhibit A</strong>
                      .
                    </li>
                    <li>
                      Monthly service fees shall be&nbsp;
                      <strong>billed and payable in advance</strong>
                      .
                    </li>
                  </>
                )}
              </ul>
            </li>
            {!isPrefill && (
              <>
                <br />
                {renderRevenueShare(offerings, revenueSharePercent, hybridDetails, isHybrid)}
              </>
            )}
            <br />
            {taxesContent}
          </ul>
        </Box>
      )}
    />
  );
};

ExhibitAModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isSmall: PropTypes.bool.isRequired,
  // Standard-mode props (required when prefillExhibitA is not set)
  offerings: PropTypes.object,
  platforms: PropTypes.array,
  channels: PropTypes.array,
  effectiveDate: PropTypes.string,
  setupFee: PropTypes.number,
  monthlyFee: PropTypes.number,
  currency: PropTypes.string,
  revenueSharePercent: PropTypes.number,
  cidPlanDisplayLabel: PropTypes.string,
  uploaderDisplayLabel: PropTypes.string,
  hybridDetails: PropTypes.shape({
    initialMonths: PropTypes.number,
    initialSharePercent: PropTypes.number,
    afterSharePercent: PropTypes.number,
  }),
  isHybrid: PropTypes.bool,
  // Prefill-mode prop
  prefillExhibitA: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      productKey: PropTypes.string,
      productLabel: PropTypes.string.isRequired,
      productType: PropTypes.string,
      plan: PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.object,
      }),
      config: PropTypes.object,
    })),
    extraItems: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.oneOf(['charge', 'discount']).isRequired,
      valueType: PropTypes.oneOf(['amount', 'percent']).isRequired,
      value: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      frequency: PropTypes.oneOf(['one_time', 'monthly']).isRequired,
      productKey: PropTypes.string,
    })),
    currency: PropTypes.string,
    effectiveDate: PropTypes.string.isRequired,
  }),
};

export default ExhibitAModal;
